import axios, {AxiosError, AxiosRequestConfig} from "axios";

/** =========================================================
 * Base
 * ======================================================= */
const BASE_URL = "/api";

/** =========================================================
 * LocalStorage keys
 * ======================================================= */
const LS_KEYS = {
  token: "token",
  user: "user",
  userId: "userId",
  email: "email",
  loginAt: "loginAt",
  expiresAt: "expiresAt",
  socialLoginInProgress: "socialLoginInProgress",
} as const;

/** =========================================================
 * Safe localStorage access
 * ======================================================= */
const safeLocalStorage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  set(key: string, value: string) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage may be unavailable in private/locked-down browser contexts.
    }
  },

  remove(key: string) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

const isSocialLoginInProgress = () =>
    safeLocalStorage.get(LS_KEYS.socialLoginInProgress) === "true";

/** =========================================================
 * Axios instances
 * ======================================================= */
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
});

/**
 * Refresh token rotation must not pass through the normal response interceptor.
 * Otherwise a failed refresh could recursively trigger another refresh.
 */
const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

/** =========================================================
 * Unauthorized event hook
 * ======================================================= */
type VoidFn = () => void;

let unauthHandlers: VoidFn[] = [];
let unauthorizedNotified = false;

export const onUnauthorized = (fn: VoidFn) => {
  unauthHandlers.push(fn);
  return () => {
    unauthHandlers = unauthHandlers.filter((h) => h !== fn);
  };
};

const notifyUnauthorized = () => {
  if (unauthorizedNotified) return;
  unauthorizedNotified = true;

  try {
    unauthHandlers.forEach((fn) => fn());
  } catch {
    // A UI handler must never break the auth transport layer.
  }
};

const resetUnauthorizedNotify = () => {
  unauthorizedNotified = false;
};

/** =========================================================
 * HTTP/auth helpers
 * ======================================================= */
export function getHttpStatus(error: unknown): number | null {
  if (!axios.isAxiosError(error)) return null;
  return typeof error.response?.status === "number" ? error.response.status : null;
}

export function isUnauthorizedError(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status === 401 || status === 419;
}

/**
 * "Client auth state" is only a hint used to distinguish a true guest from a
 * browser that still looks signed-in locally. The server remains authoritative.
 */
export function hasClientAuthState(): boolean {
  return Boolean(
      tokenStore.get()?.trim()
      || safeLocalStorage.get(LS_KEYS.user)
      || safeLocalStorage.get(LS_KEYS.userId)
      || safeLocalStorage.get(LS_KEYS.loginAt)
  );
}

/** =========================================================
 * Refresh single-flight + activity tracking
 * ======================================================= */
let refreshPromise: Promise<string> | null = null;
let lastActivityAt = Date.now();

const ACTIVE_WINDOW_MS = 10 * 60 * 1000;
const REFRESH_LEAD_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "pointerdown"] as const;

const markUserActivity = () => {
  lastActivityAt = Date.now();
};

if (typeof window !== "undefined") {
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, markUserActivity, {passive: true});
  });
}

const wasRecentlyActive = () => Date.now() - lastActivityAt <= ACTIVE_WINDOW_MS;

/** =========================================================
 * JWT exp decoder + proactive refresh
 * ======================================================= */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function decodeJwtExp(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === "number" ? payload.exp : null;
}

export function getTokenExpiresAt(token: string): number | null {
  const exp = decodeJwtExp(token);
  return exp ? exp * 1000 : null;
}

let refreshTimer: number | null = null;

function clearRefreshTimer() {
  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleAccessRefresh(token: string) {
  clearRefreshTimer();

  const expiresAt = getTokenExpiresAt(token);
  if (!expiresAt || typeof window === "undefined") return;

  const delayMs = Math.max(0, expiresAt - Date.now() - REFRESH_LEAD_MS);

  refreshTimer = window.setTimeout(async () => {
    if (isSocialLoginInProgress()) return;

    if (!wasRecentlyActive()) {
      // Do not extend an inactive session forever. Re-check while the token still
      // exists; an actual authenticated request can also recover through refresh.
      refreshTimer = window.setTimeout(() => {
        const current = tokenStore.get();
        if (current) scheduleAccessRefresh(current);
      }, 60_000);
      return;
    }

    if (!hasClientAccessToken()) {
      clearRefreshTimer();
      return;
    }

    try {
      await refreshAccessToken();
    } catch {
      if (!isSocialLoginInProgress()) {
        clearClientAuthState();
        notifyUnauthorized();
      }
    }
  }, delayMs);
}

/**
 * Refresh token rotation is owned by this function only.
 * Concurrent callers share one Promise so a rotating refresh token is used once.
 */
export function refreshAccessToken(options?: {
  allowDuringSocialLogin?: boolean;
}): Promise<string> {
  if (refreshPromise) return refreshPromise;

  if (isSocialLoginInProgress() && !options?.allowDuringSocialLogin) {
    return Promise.reject(new Error("Social login is still in progress"));
  }

  refreshPromise = refreshClient
  .post("/user/refresh", null, {
    headers: {"X-Requested-With": "XMLHttpRequest"},
  })
  .then((response) => {
    const data = response.data as {
      token?: string;
      accessToken?: string;
      access_token?: string;
      expiresAt?: number;
    };

    const newToken =
        data?.token
        ?? data?.accessToken
        ?? data?.access_token
        ?? null;

    if (!newToken) {
      throw new Error("No token in refresh response");
    }

    setAccessToken(newToken, data?.expiresAt);

    if (typeof window !== "undefined") {
      window.dispatchEvent(
          new CustomEvent("auth:token-refreshed", {
            detail: {
              token: newToken,
              expiresAt: data?.expiresAt ?? getTokenExpiresAt(newToken),
            },
          })
      );
    }

    return newToken;
  })
  .finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/** =========================================================
 * Client-side auth cleanup
 * ======================================================= */
export function clearClientAuthState() {
  ACCESS_TOKEN = null;

  safeLocalStorage.remove(LS_KEYS.token);
  safeLocalStorage.remove(LS_KEYS.user);
  safeLocalStorage.remove(LS_KEYS.userId);
  safeLocalStorage.remove(LS_KEYS.email);
  safeLocalStorage.remove(LS_KEYS.loginAt);
  safeLocalStorage.remove(LS_KEYS.expiresAt);

  delete api.defaults.headers.common.Authorization;
  clearRefreshTimer();
}

/** =========================================================
 * Token bridge
 * ======================================================= */
let ACCESS_TOKEN: string | null = null;

export const tokenStore = {
  get: () => ACCESS_TOKEN ?? safeLocalStorage.get(LS_KEYS.token),

  set: (token: string | null, expiresAtFromServer?: number | null) => {
    ACCESS_TOKEN = token;

    if (!token) {
      clearClientAuthState();
      return;
    }

    safeLocalStorage.set(LS_KEYS.token, token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;

    const decodedExpiresAt = getTokenExpiresAt(token);
    const expiresAt =
        typeof expiresAtFromServer === "number" && Number.isFinite(expiresAtFromServer)
            ? expiresAtFromServer
            : decodedExpiresAt;

    if (expiresAt) {
      safeLocalStorage.set(LS_KEYS.expiresAt, String(expiresAt));
    } else {
      safeLocalStorage.remove(LS_KEYS.expiresAt);
    }

    resetUnauthorizedNotify();
    scheduleAccessRefresh(token);
  },
};

/** Public token setter used by login/social-login API modules. */
export function setAccessToken(token: string | null, expiresAtFromServer?: number | null) {
  tokenStore.set(token, expiresAtFromServer);
}

export function getAccessToken(): string | null {
  return tokenStore.get();
}

const hasClientAccessToken = () => Boolean(tokenStore.get()?.trim());

(() => {
  const boot = safeLocalStorage.get(LS_KEYS.token);
  const bootExpiresAtRaw = safeLocalStorage.get(LS_KEYS.expiresAt);
  const bootExpiresAt = bootExpiresAtRaw ? Number(bootExpiresAtRaw) : null;

  if (boot) {
    setAccessToken(boot, Number.isFinite(bootExpiresAt) ? bootExpiresAt : null);
  }
})();

/** =========================================================
 * Request/response auth policy
 * ======================================================= */
const isRefreshUrl = (url?: string) => Boolean(url && /\/user\/refresh\b/.test(url));

/**
 * These endpoints establish/recover identity and must not accidentally inherit a
 * stale Bearer token or trigger a refresh because their own 401 is meaningful.
 */
const isPublicAuthUrl = (url?: string) => Boolean(
    url && /\/user\/(?:login|signup|refresh|verify-email|request-password-reset|validate-reset-token|reset-password|find-email)\b/.test(url)
);

api.interceptors.request.use((config) => {
  const url = config.url || "";

  if (!isPublicAuthUrl(url)) {
    const token = tokenStore.get();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  } else if (config.headers) {
    // Prevent an axios default Authorization header from leaking into login/signup.
    try {
      delete (config.headers as Record<string, unknown>).Authorization;
    } catch {
      // AxiosHeaders can be implementation-specific; request still proceeds.
    }
  }

  return config;
});

api.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const original = (error.config || {}) as AxiosRequestConfig & { _retry?: boolean };
      const url = original.url || "";

      if (!status) throw error;

      const unauthorized = status === 401 || status === 419;
      if (!unauthorized) throw error;

      // A credential-entry endpoint's 401 is not "session expired".
      if (isPublicAuthUrl(url) && !isRefreshUrl(url)) {
        throw error;
      }

      const socialLoginNow = isSocialLoginInProgress();
      if (socialLoginNow && !isRefreshUrl(url)) {
        throw error;
      }

      if (isRefreshUrl(url)) {
        clearClientAuthState();
        notifyUnauthorized();
        throw error;
      }

      // The request was already retried with a freshly rotated access token and
      // still failed. Server auth is authoritative: clear the stale UI state now.
      if (original._retry) {
        const lookedAuthenticated = hasClientAuthState();
        clearClientAuthState();
        if (lookedAuthenticated) notifyUnauthorized();
        throw error;
      }

      // True guests must not manufacture a refresh request. If local UI/storage
      // still claims a user while no access token exists, clear that stale state
      // and notify the app so it can render SESSION_EXPIRED instead of logged-in.
      if (!hasClientAccessToken()) {
        const lookedAuthenticated = hasClientAuthState();
        if (lookedAuthenticated) {
          clearClientAuthState();
          notifyUnauthorized();
        }
        throw error;
      }

      original._retry = true;

      try {
        const newToken = await refreshAccessToken();
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshError) {
        clearClientAuthState();
        notifyUnauthorized();
        throw refreshError;
      }
    }
);

export default api;
